import { Reveal } from "./Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "No vertical integration",
    body: "We are not owned by a bank, a fund manager or an insurer. There is no parent company whose products we are quietly expected to place, and no sales targets sitting behind the advice you receive.",
    span: "md:col-span-7",
    testid: "chapter-no-vertical-integration",
  },
  {
    n: "02",
    title: "No asset-based fees",
    body: "Our fees are fixed and agreed with you in advance — never a percentage of your portfolio. Your adviser has no financial incentive to gather assets. The only incentive is to give good advice.",
    span: "md:col-span-5 md:mt-24",
    testid: "chapter-no-asset-fees",
  },
  {
    n: "03",
    title: "No product promotion",
    body: "We accept no commissions, rebates or shelf-space payments from product providers. If a product is recommended, it earned its place on merit — and it can be replaced the moment something serves you better.",
    span: "md:col-span-7 md:-mt-12",
    testid: "chapter-no-product-promotion",
  },
  {
    n: "04",
    title: "Only your circumstances",
    body: "Every recommendation begins with your goals, your family and your stage of life. Personal advice, in the truest sense of the words.",
    span: "md:col-span-7 md:col-start-6 md:mt-8",
    testid: "chapter-only-circumstances",
  },
];

const Chapter = ({ n, title, body, span, testid }) => (
  <Reveal className={span}>
    <article data-testid={testid} className="border-t border-black/15 pt-8">
      <span className="font-serif italic text-6xl md:text-7xl leading-none text-[#A78558]">{n}</span>
      <h3 className="mt-6 font-serif text-3xl md:text-4xl tracking-tight text-[#111413]">{title}</h3>
      <p className="mt-5 text-base leading-relaxed text-[#4A524D] max-w-xl">{body}</p>
    </article>
  </Reveal>
);

export const Manifesto = () => (
  <section id="charter" data-testid="charter-section" className="bg-[#F9F8F4] py-16 md:py-36">
    <div className="mx-auto max-w-7xl px-6 md:px-10">
      <div data-testid="charter-divider" aria-hidden="true" className="flex items-center justify-center gap-6 mb-14 md:mb-20">
        <span className="h-px w-16 md:w-28 bg-[#0A2333]/15" />
        <img src="/assets/nua-mark-transparent.png" alt="" className="h-9 w-auto opacity-90" />
        <span className="h-px w-16 md:w-28 bg-[#0A2333]/15" />
      </div>
      <Reveal>
        <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#0A2333]">
          <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
          The Independence Charter
        </p>
        <h2 data-testid="charter-heading" className="mt-6 font-serif text-3xl md:text-4xl tracking-tight text-[#111413] max-w-xl">
          What independence actually means.
        </h2>
        <p className="mt-6 text-base leading-relaxed text-[#4A524D] max-w-xl">
          In Australian financial advice, the word “independent” is legally restricted under
          Section 923A of the Corporations Act 2001. Because of these strict requirements, less
          than 1% of financial advisers nationwide can legally use the term. We can. Here is the
          standard we hold ourselves to, in four chapters.
        </p>
      </Reveal>

      <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
        <Chapter {...CHAPTERS[0]} />
        <Chapter {...CHAPTERS[1]} />
        <Reveal className="md:col-span-5 md:-mt-12">
          <div className="overflow-hidden border border-black/10" data-testid="charter-image-frame">
            <img
              src="https://images.unsplash.com/photo-1496865534669-25ec2a3a0fd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NzkwOTc4Mnww&ixlib=rb-4.1.0&q=85"
              alt="Clean minimalist architecture — a metaphor for structure without hidden interests"
              className="h-64 md:h-96 w-full object-cover transition-transform duration-700 hover:scale-105"
              data-testid="charter-image"
            />
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-[#4A524D]">
            Structure, without hidden interests
          </p>
        </Reveal>
        <Chapter {...CHAPTERS[2]} />
        <Chapter {...CHAPTERS[3]} />
      </div>
    </div>
  </section>
);
